import React, { useEffect, useMemo, useState } from 'react';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useAuth0 } from '@auth0/auth0-react';
import { useSearchParams } from 'react-router-dom';
import { IAdminTask, useAdminTaskAssigneesQuery, useAdminTasksQuery } from '../../api/admin';
import { toIsoDate } from '../../utils/dates';
import { AppCard, AppPageHeader } from '../../components';
import { TaskDialog, TaskQuickAdd, TaskSummaryEmailMenu, TasksTable } from '../../components/Admin';
import { assigneeColor, userLabel } from '../../components/Admin/taskOptions';

type StatusFilter = 'open' | 'week' | 'done' | 'all';
/** 'all', 'none' (unassigned) or a user id. */
type AssigneeFilter = 'all' | 'none' | number;

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
	{ value: 'open', label: 'Open' },
	{ value: 'week', label: 'Open + done this week' },
	{ value: 'done', label: 'Done' },
	{ value: 'all', label: 'All' },
];
const VALID_STATUS = STATUS_FILTERS.map((f) => f.value);
const DEFAULT_STATUS: StatusFilter = 'week';

function matchesStatus(task: IAdminTask, status: StatusFilter, weekAgo: string): boolean {
	switch (status) {
		case 'open':
			return task.status !== 'done';
		case 'done':
			return task.status === 'done';
		case 'all':
			return true;
		default:
			return task.status !== 'done' || (task.completed_date !== null && task.completed_date >= weekAgo);
	}
}

function matchesAssignee(task: IAdminTask, assignee: AssigneeFilter): boolean {
	if (assignee === 'all') return true;
	if (assignee === 'none') return task.assignee_user_id === null;
	return task.assignee_user_id === assignee;
}

/** The weekly catch-up view: add tasks in one line, change them in the grid, narrow by assignee and status. */
export default function TasksPage() {
	const [searchParams, setSearchParams] = useSearchParams();
	const { user } = useAuth0();
	const { data: tasks, isLoading } = useAdminTasksQuery();
	const { data: assignees = [] } = useAdminTaskAssigneesQuery();
	const [createOpen, setCreateOpen] = useState(false);
	const [highlightId, setHighlightId] = useState<number | null>(null);

	// The flash is a one-off: clear it once the animation has played so a remounted row does not replay it.
	useEffect(() => {
		if (highlightId === null) return;
		const handle = setTimeout(() => setHighlightId(null), 2000);
		return () => clearTimeout(handle);
	}, [highlightId]);

	// Filters live in the URL so a view survives refresh and can be bookmarked; defaults stay out of it.
	const rawAssignee = searchParams.get('assignee');
	const assignee: AssigneeFilter =
		rawAssignee === 'none' ? 'none' : rawAssignee && /^\d+$/.test(rawAssignee) ? Number(rawAssignee) : 'all';
	const rawStatus = searchParams.get('status') as StatusFilter | null;
	const status: StatusFilter = rawStatus && VALID_STATUS.includes(rawStatus) ? rawStatus : DEFAULT_STATUS;

	function setFilters(next: { assignee?: AssigneeFilter; status?: StatusFilter }) {
		const params: Record<string, string> = {};
		const a = next.assignee ?? assignee;
		const s = next.status ?? status;
		if (a !== 'all') params.assignee = String(a);
		if (s !== DEFAULT_STATUS) params.status = s;
		setSearchParams(params, { replace: true });
	}

	const myId = useMemo(() => {
		const email = user?.email?.toLowerCase();
		return assignees.find((u) => u.email.toLowerCase() === email)?.id ?? null;
	}, [assignees, user]);

	const visible = useMemo(() => {
		const d = new Date();
		d.setDate(d.getDate() - 7);
		const weekAgo = toIsoDate(d);
		return (tasks ?? []).filter((t) => matchesAssignee(t, assignee) && matchesStatus(t, status, weekAgo));
	}, [tasks, assignee, status]);

	return (
		<>
			<AppPageHeader>
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
					<Typography variant="h5">Tasks</Typography>
					<Box sx={{ display: 'flex', gap: 1.5 }}>
						<TaskSummaryEmailMenu />
						<Button variant="outlined" color="success" startIcon={<PlaylistAddIcon />} onClick={() => setCreateOpen(true)}>
							Add with details
						</Button>
					</Box>
				</Box>
			</AppPageHeader>
			<AppCard>
				<TaskQuickAdd assignees={assignees} defaultAssigneeId={myId} onAdded={(t) => setHighlightId(t.id)} />
				<Divider sx={{ mb: 2 }} />
				<Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 3, flexWrap: 'wrap', mb: 1 }}>
					<Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, pb: 0.75 }}>
						Filters
					</Typography>
					<TextField
						select
						variant="standard"
						size="small"
						label="Assignee"
						value={String(assignee)}
						onChange={(e) => {
							const v = e.target.value;
							setFilters({ assignee: v === 'all' || v === 'none' ? v : Number(v) });
						}}
						sx={{ width: 220 }}
					>
						<MenuItem value="all">All</MenuItem>
						{assignees.map((u) => (
							<MenuItem key={u.id} value={String(u.id)}>
								<Box
									component="span"
									sx={{
										width: 10,
										height: 10,
										borderRadius: '50%',
										bgcolor: assigneeColor(u.id, assignees),
										flexShrink: 0,
										mr: 1,
									}}
								/>
								{userLabel(u)}
								{u.id === myId ? ' (me)' : ''}
							</MenuItem>
						))}
						<MenuItem value="none">Unassigned</MenuItem>
					</TextField>
					<TextField
						select
						variant="standard"
						size="small"
						label="Status"
						value={status}
						onChange={(e) => setFilters({ status: e.target.value as StatusFilter })}
						sx={{ width: 220 }}
					>
						{STATUS_FILTERS.map((f) => (
							<MenuItem key={f.value} value={f.value}>
								{f.label}
							</MenuItem>
						))}
					</TextField>
					<Typography variant="body2" color="text.secondary" sx={{ ml: 'auto', pb: 0.5 }}>
						{tasks ? `${visible.length} of ${tasks.length}` : ''}
					</Typography>
				</Box>
				<TasksTable tasks={visible} loading={isLoading} assignees={assignees} highlightId={highlightId} />
			</AppCard>
			<TaskDialog open={createOpen} onClose={() => setCreateOpen(false)} defaultAssigneeId={myId} />
		</>
	);
}
